import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Building2, 
  Users, 
  CreditCard, 
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  Loader2,
  RefreshCw,
  Plus
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { ApiService } from '@/services/api'
import { useNavigate } from 'react-router-dom'
import NotificationCenter from '@/components/common/NotificationCenter'
import type { Restaurant } from '@/types'

interface SystemAnalytics {
  totalRestaurants: number
  activeSubscriptions: number
  expiredSubscriptions: number
  totalRevenue: number
  expiringSubscriptions: Restaurant[]
}

interface RenewalRequest {
  _id: string
  restaurantId: string
  restaurantName: string
  ownerName: string
  ownerEmail: string
  ownerPhone: string
  currentPlan: string
  currentAmount: number
  subscriptionEndDate: Date
  daysRemaining: number
  requestedAt: Date
  message: string
  status: 'pending' | 'contacted' | 'completed'
  adminNotes?: string
  createdAt: Date
  updatedAt: Date
}

export default function AdminDashboardScreen() {
  const navigate = useNavigate()
  const [analytics, setAnalytics] = useState<SystemAnalytics | null>(null)
  const [renewalRequests, setRenewalRequests] = useState<RenewalRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      // Load both analytics and renewal requests
      const [analyticsResponse, renewalResponse] = await Promise.all([
        ApiService.getSystemAnalytics(),
        ApiService.getRenewalRequests('pending')
      ])
      
      if (analyticsResponse.success) {
        setAnalytics(analyticsResponse.data)
      } else {
        setError(analyticsResponse.message || 'Failed to load dashboard data')
      }

      if (renewalResponse.success) {
        setRenewalRequests(renewalResponse.data)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load dashboard data')
    } finally {
      setIsLoading(false)
    }
  }

  const getDaysRemaining = (endDate: Date) => {
    const today = new Date()
    const end = new Date(endDate)
    const diffTime = end.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const handleRenewSubscription = async (restaurantId: string) => {
    try {
      const updateData = {
        subscriptionStatus: 'active' as const,
        subscriptionStartDate: new Date(),
        subscriptionEndDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        paymentStatus: 'paid' as const,
        isActive: true
      }

      const response = await ApiService.updateRestaurant(restaurantId, updateData)
      
      if (response.success) {
        await loadDashboardData() // Refresh the dashboard data
      }
    } catch (err: any) {
      console.error('Failed to renew subscription:', err)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading dashboard...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-2"
              onClick={loadDashboardData}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No dashboard data available. Please check your connection and try again.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const systemMetrics = [
    {
      title: 'Total Restaurants',
      value: analytics.totalRestaurants.toString(),
      description: 'Registered restaurants',
      icon: Building2,
      color: 'text-blue-600',
    },
    {
      title: 'Active Subscriptions',
      value: analytics.activeSubscriptions.toString(),
      description: 'Currently active',
      icon: CheckCircle,
      color: 'text-green-600',
    },
    {
      title: 'Expired Subscriptions',
      value: analytics.expiredSubscriptions.toString(),
      description: 'Require renewal',
      icon: AlertTriangle,
      color: 'text-red-600',
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(analytics.totalRevenue),
      description: 'Total earned',
      icon: DollarSign,
      color: 'text-green-600',
    },
  ]

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            System overview and restaurant management
          </p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={() => navigate('/admin/restaurants')}>
            <Plus className="mr-2 h-4 w-4" />
            Add Restaurant
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/subscriptions')}>
            <TrendingUp className="mr-2 h-4 w-4" />
            View Analytics
          </Button>
        </div>
      </div>

      {/* System Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {systemMetrics.map((metric) => (
          <Card key={metric.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {metric.title}
              </CardTitle>
              <metric.icon className={`h-4 w-4 ${metric.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metric.value}</div>
              <p className="text-xs text-muted-foreground">
                {metric.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Restaurant Management</CardTitle>
            <CardDescription>
              Add, edit, and manage restaurant accounts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full"
              onClick={() => navigate('/admin/restaurants')}
            >
              <Building2 className="mr-2 h-4 w-4" />
              Manage Restaurants
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Subscription Control</CardTitle>
            <CardDescription>
              Activate, renew, and manage subscriptions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/admin/subscriptions')}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Manage Subscriptions  
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">User Management</CardTitle>
            <CardDescription>
              Create and manage user accounts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/admin/users')}
            >
              <Users className="mr-2 h-4 w-4" />
              Manage Users
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Subscriptions Alert */}
      <Card className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-orange-800 dark:text-orange-200">
              Subscriptions Expiring Soon
            </CardTitle>
          </div>
          <CardDescription className="text-orange-600 dark:text-orange-300">
            {analytics.expiringSubscriptions.length} restaurants have subscriptions expiring within 30 days
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {analytics.expiringSubscriptions.map((restaurant) => {
              const daysRemaining = getDaysRemaining(restaurant.subscriptionEndDate)
              return (
                <div key={restaurant._id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{restaurant.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Owner: {restaurant.ownerName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Expires in {daysRemaining} days
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={daysRemaining <= 7 ? 'destructive' : 'warning'}>
                      {daysRemaining} days left
                    </Badge>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleRenewSubscription(restaurant._id)}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Renew
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
          {analytics.expiringSubscriptions.length === 0 && (
            <div className="text-center py-4 text-muted-foreground">
              No subscriptions expiring soon
            </div>
          )}
          <div className="mt-4">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/admin/subscriptions')}
            >
              View All Subscriptions
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Renewal Requests */}
      {renewalRequests.length > 0 && (
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <RefreshCw className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-blue-800 dark:text-blue-200">
                Renewal Requests
              </CardTitle>
            </div>
            <CardDescription className="text-blue-600 dark:text-blue-300">
              {renewalRequests.length} restaurants have requested subscription renewal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {renewalRequests.slice(0, 5).map((request) => (
                <div key={request._id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{request.restaurantName}</p>
                      <p className="text-sm text-muted-foreground">
                        Owner: {request.ownerName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Email: {request.ownerEmail}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Expires in {request.daysRemaining} days • Requested {new Date(request.requestedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="bg-blue-100 text-blue-800">
                      {request.currentPlan}
                    </Badge>
                    <Badge variant="secondary">
                      {formatCurrency(request.currentAmount)}
                    </Badge>
                    <Button 
                      size="sm" 
                      variant="default"
                      onClick={() => handleRenewSubscription(request.restaurantId)}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Renew Now
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {renewalRequests.length > 5 && (
              <div className="mt-3 text-center text-sm text-muted-foreground">
                Showing 5 of {renewalRequests.length} renewal requests
              </div>
            )}
            <div className="mt-4">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/admin/subscriptions')}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                View All Renewal Requests
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>System Activity</CardTitle>
          <CardDescription>
            Recent system events and statistics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="text-center p-4 rounded-lg bg-muted">
              <div className="text-2xl font-bold text-blue-600">
                {analytics.totalRestaurants}
              </div>
              <div className="text-sm text-muted-foreground">Total Restaurants</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted">
              <div className="text-2xl font-bold text-green-600">
                {analytics.totalRestaurants > 0 
                  ? Math.round((analytics.activeSubscriptions / analytics.totalRestaurants) * 100)
                  : 0}%
              </div>
              <div className="text-sm text-muted-foreground">Active Rate</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted">
              <div className="text-2xl font-bold text-purple-600">
                ₹{(analytics.totalRevenue / 1000000).toFixed(1)}M
              </div>
              <div className="text-sm text-muted-foreground">Total Revenue</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted">
              <div className="text-2xl font-bold text-orange-600">
                {analytics.expiringSubscriptions.length}
              </div>
              <div className="text-sm text-muted-foreground">Expiring Soon</div>
            </div>
          </div>
          <div className="mt-4 grid gap-2 md:grid-cols-2">
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/restaurants')}
              className="w-full"
            >
              <Building2 className="mr-2 h-4 w-4" />
              View All Restaurants
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/subscriptions')}
              className="w-full"
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              Analytics & Reports
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Center */}
      <NotificationCenter />
    </div>
  )
}